import React from "react";
import { useTranslation } from "react-i18next";
import { Button, Popconfirm, Space, Switch, Table, Tag, Typography } from "antd";
import type { ColumnType } from "antd/es/table";
import { DeleteOutlined } from "@ant-design/icons";

import { useAntdTheme } from "../../shared/hooks";
import { PointsConfig } from "../points-config";
import { MoveButtons } from "../combos/components";
import { MenuItemIndex, computeComboPreview, formatCents } from "../combos/pricing";
import { ComboAddonSchemaWithUsage } from "../combos/types";
import { ImageUploadTrigger } from "./ImageUploadCell";
import { SavedCombo } from "./useMenuCombos";

const { Text } = Typography;

type SectionCombosProps = {
  restaurantId: string;
  // The section's combos, sorted by seq.
  combos: SavedCombo[];
  addons: ReadonlyArray<ComboAddonSchemaWithUsage>;
  // Items of the section's menu type.
  items: MenuItemIndex;
  pointsConfig: PointsConfig | undefined;
  busyIds: ReadonlySet<string>;
  onEdit: (combo: SavedCombo) => void;
  onSoldOut: (combo: SavedCombo, soldOut: boolean) => void;
  onMove: (from: number, to: number) => void;
  onImage: (combo: SavedCombo, manifest: SavedCombo["image_manifest"]) => Promise<void>;
  onDelete: (combo: SavedCombo) => void;
};

/** A menu section's combos, listed above its items. */
export const SectionCombos: React.FC<SectionCombosProps> = ({
  restaurantId,
  combos,
  addons,
  items,
  pointsConfig,
  busyIds,
  onEdit,
  onSoldOut,
  onMove,
  onImage,
  onDelete,
}) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const addonById = new Map(addons.map((a) => [a.id, a]));

  const columns: ColumnType<SavedCombo>[] = [
    {
      title: t("image"),
      width: 80,
      render: (_: unknown, combo) => (
        <ImageUploadTrigger
          restaurantId={restaurantId}
          imageUrl={combo.image_manifest?.default.url}
          alt={combo.name}
          onUploaded={(manifest) => onImage(combo, manifest)}
        />
      ),
    },
    {
      title: t("menu_item_name"),
      render: (_: unknown, combo) => (
        <Space size={4}>
          <Tag color="purple">{t("combo_badge")}</Tag>
          <Button type="link" style={{ padding: 0 }} onClick={() => onEdit(combo)}>
            {combo.name}
          </Button>
        </Space>
      ),
    },
    {
      title: t("price"),
      width: 130,
      render: (_: unknown, combo) => {
        const preview = computeComboPreview(combo, addonById, items, pointsConfig);
        return preview.startingPrice === null ? (
          <Text type="danger">{t("combo_needs_entree")}</Text>
        ) : (
          <Text>{t("combo_from_price", { price: formatCents(preview.startingPrice) })}</Text>
        );
      },
    },
    {
      title: t("sold_out"),
      width: 100,
      render: (_: unknown, combo) => (
        <Switch
          size="small"
          aria-label={t("sold_out")}
          checked={combo.sold_out}
          loading={busyIds.has(combo.id)}
          onChange={(checked) => onSoldOut(combo, checked)}
        />
      ),
    },
    {
      title: "",
      width: 130,
      align: "center" as const,
      render: (_: unknown, combo, index) => (
        <Space size={0}>
          <MoveButtons index={index} count={combos.length} onMove={onMove} />
          <Popconfirm
            title={t("combo_delete_confirm", { name: combo.name })}
            okText={t("yes")}
            cancelText={t("no")}
            onConfirm={() => onDelete(combo)}
          >
            <Button shape="circle" aria-label={t("delete")} icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Table
      data-testid="section-combos"
      columns={columns}
      dataSource={combos}
      rowKey={(combo) => combo.id}
      pagination={false}
      size="small"
      style={{ marginBottom: token.marginMD, borderRadius: token.borderRadiusLG }}
    />
  );
};
